import Modal from "@/components/controls/Modal";
import { capitalize, forEach } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { DealData, DealStatus } from "@/pages/deals/types";
import Form, {
  FormField,
  SingleSelectOptions
} from "@/components/controls/Form/Form";
import { closedLostReasonsFromIdx } from "@/pages/deals/helpers";
import { CloseWonIllustration } from "@dreamhub/designsystem/icons";
import { useClosedLostReasons } from "@/entities/settings/deal/hooks";
import { ClosedLostDreamerIndication } from "@/pages/deals/components/ClosedLostDreamerIndication";
import {
  ClosedLostReasonAnalysis,
  ClosedLostReasonSuggestion,
  parseClosedLostReasonText,
  useClosedLostReasonSuggestion
} from "@/pages/deals/hooks";
import { useUsersMe } from "@/entities/users/hooks";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  itemToClose: DealData | null;
  closeDeal: (
    item: DealData | null,
    status: DealStatus.CLOSED_LOST | DealStatus.CLOSED_WON,
    data?: {
      reason: number;
      comment: string;
    },
    sourceColumnId?: number | null,
    isEditMode?: boolean
  ) => void;
  status: DealStatus.CLOSED_LOST | DealStatus.CLOSED_WON;
  sourceColumnId?: number | null;
  selectedReason?: number | null;
  closedLostReasonText?: string | null;
}

const CloseDealModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  itemToClose,
  closeDeal,
  status,
  sourceColumnId,
  selectedReason,
  closedLostReasonText
}) => {
  const { t, i18n } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const setFormValueRef = useRef<((name: string, value: string) => void) | null>(null);
  // Component-scope abort token — incremented on each new suggestion fetch so any
  // in-flight promise from a previous open/deal can be invalidated on re-open.
  const requestTokenRef = useRef(0);

  const isClosedWon = status === DealStatus.CLOSED_WON;
  const isEditMode = selectedReason != null;

  const closedLostReasons =
    useClosedLostReasons().data?.closedLostReasons ?? [];
  const [isFormValid, setIsFormValid] = useState(false);
  // Tracks the reason currently selected in the form. Initialized from selectedReason
  // when the modal opens so the badge reflects the pre-filled value immediately
  // (avoids a flash before the first isFormValid fires).
  const [selectedFormReasonId, setSelectedFormReasonId] = useState<number | null>(null);

  const currentUser = useUsersMe().data;

  const { analysis: existingAnalysis } =
    parseClosedLostReasonText(closedLostReasonText);
  const existingNote = existingAnalysis
    ? existingAnalysis.userNote || ""
    : closedLostReasonText || "";

  const suggestionMutation = useClosedLostReasonSuggestion();
  const [suggestion, setSuggestion] =
    useState<ClosedLostReasonSuggestion | null>(null);
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false);

  useEffect(() => {
    if (!(isOpen && !isClosedWon && !isEditMode && itemToClose?.id)) return;

    const token = ++requestTokenRef.current;
    setSuggestion(null);
    setIsSuggestionLoading(true);

    suggestionMutation
      .mutateAsync({ pathParams: { dealId: itemToClose.id } })
      .then((response) => {
        if (token === requestTokenRef.current && response.hasUpdate) {
          setSuggestion(response);
        }
      })
      .catch(() => {
        // Suggestion is best-effort — don't block the modal
      })
      .finally(() => {
        if (token === requestTokenRef.current) setIsSuggestionLoading(false);
      });

    return () => {
      // Increment on cleanup so any pending .then() from this open is ignored
      requestTokenRef.current++;
    };
  }, [isOpen, isClosedWon, isEditMode, itemToClose?.id]);

  const suggestedReasonId =
    !isEditMode && suggestion?.winnerId != null
      ? suggestion.winnerId
      : null;

  // The only place Dreamer's reason is written into the form — the prefill
  // effect and the badge's restore action both go through here.
  const acceptSuggestion = (reasonId: number) => {
    setFormValueRef.current?.("reason", reasonId.toString());
  };

  const getClosedLostReasonsOptions = () => {
    const closedLostReasonsOptions: SingleSelectOptions[] = [];
    forEach(closedLostReasons, (reasonValue) => {
      closedLostReasonsOptions.push({
        id: reasonValue.id,
        displayName: closedLostReasonsFromIdx(
          t,
          i18n,
          closedLostReasons,
          reasonValue.id
        )
      });
    });
    return closedLostReasonsOptions;
  };

  const showDreamerBadge =
    suggestedReasonId != null ||
    isSuggestionLoading ||
    existingAnalysis?.suggestedByDreamer;

  // Reference point for "what Dreamer originally suggested":
  // - New close: suggestedReasonId (fresh async response)
  // - Edit mode: existingAnalysis.suggestedReasonId (serialized in closedLostReasonText)
  const dreamerOriginalReasonId =
    suggestedReasonId ??
    (isEditMode && existingAnalysis?.suggestedByDreamer
      ? (existingAnalysis.suggestedReasonId ?? null)
      : null);

  // User has overridden the Dreamer suggestion if there was one, the form has a
  // selection, and the selection differs from what Dreamer recommended.
  const isDreamerOverriddenInModal =
    dreamerOriginalReasonId != null &&
    selectedFormReasonId != null &&
    selectedFormReasonId !== dreamerOriginalReasonId;

  const dreamerJustification =
    suggestion?.justification || existingAnalysis?.justification || null;
  const dreamerCitation =
    suggestion?.citation ?? existingAnalysis?.citation ?? null;

  const dreamerBadge = showDreamerBadge ? (
    isDreamerOverriddenInModal ? (
      // User has overridden the Dreamer suggestion — dual avatar, clickable to restore
      <ClosedLostDreamerIndication
        justification={dreamerJustification ?? ""}
        citation={dreamerCitation}
        size="sm"
        modifiedByUserData={currentUser}
        onRestore={() => {
          if (dreamerOriginalReasonId != null) {
            acceptSuggestion(dreamerOriginalReasonId);
          }
        }}
        showChip
      />
    ) : dreamerJustification ? (
      <ClosedLostDreamerIndication
        justification={dreamerJustification}
        citation={dreamerCitation}
        size="sm"
      />
    ) : isSuggestionLoading ? (
      // Waiting for Dreamer suggestion — show a spinner styled like the DreamerIcon container
      <div className="size-[20px] border-[0.5px] border-border-border2 rounded-lg p-[1.5px] flex items-center justify-center">
        <div className="size-3 border border-current border-t-transparent rounded-full animate-spin text-text-secondary" />
      </div>
    ) : null
  ) : null;

  const formFields: FormField[] = [
    {
      name: "reason",
      label: t("deals.modal.reason"),
      type: "singleSelect",
      required: true,
      placeholder: t("deals.modal.reasonPlaceholder"),
      options: getClosedLostReasonsOptions(),
      defaultValue: selectedReason?.toString() || "",
      labelAdornment: dreamerBadge ?? undefined
    },
    {
      name: "comment",
      label: t("deals.modal.comment"),
      type: "textarea",
      placeholder: t("deals.modal.commentPlaceholder"),
      defaultValue: existingNote || ""
    }
  ];

  // The suggestion may fill the reason field, never replace what is in it. It
  // arrives seconds after the modal opens, by which time the user may already
  // have chosen — and their choice wins.
  useEffect(() => {
    if (suggestedReasonId == null) return;
    if (selectedFormReasonId != null) return;

    acceptSuggestion(suggestedReasonId);
    // Include isOpen and itemToClose?.id so re-opening for a deal with the same
    // winnerId still pre-fills the form (suggestedReasonId alone wouldn't change).
  }, [suggestedReasonId, selectedFormReasonId, isOpen, itemToClose?.id]);

  useEffect(() => {
    if (!isOpen) {
      formRef.current?.reset();
      setSuggestion(null);
      setIsSuggestionLoading(false);
      setSelectedFormReasonId(null);
    } else {
      // Pre-initialize from the pre-filled value so the badge state is correct
      // immediately, before the first isFormValid callback fires.
      setSelectedFormReasonId(selectedReason ?? null);
    }
  }, [isOpen, selectedReason, itemToClose?.id]);

  const handleSubmit = (data: { reason: string; comment: string }) => {
    const reasonId = Number(data.reason);
    const usedSuggestion =
      suggestion && suggestedReasonId != null && reasonId === suggestedReasonId;

    let comment = data.comment;
    if (usedSuggestion) {
      // Fresh Dreamer suggestion accepted — serialize the full analysis.
      const analysis: ClosedLostReasonAnalysis = {
        suggestedByDreamer: true,
        justification: suggestion.justification,
        citation: suggestion.citation ?? null,
        userNote: data.comment || null,
        suggestedReasonId: suggestion.winnerId
      };
      comment = JSON.stringify(analysis);
    } else if (isEditMode && existingAnalysis?.suggestedByDreamer) {
      // User editing a Dreamer-suggested reason — preserve original Dreamer data
      // so the indication can still show justification/citation and detect that
      // this was originally suggested by Dreamer (last_modified_by → human user).
      const analysis: ClosedLostReasonAnalysis = {
        suggestedByDreamer: true,
        justification: existingAnalysis.justification,
        citation: existingAnalysis.citation,
        userNote: data.comment || null,
        suggestedReasonId: existingAnalysis.suggestedReasonId ?? null
      };
      comment = JSON.stringify(analysis);
    }

    closeDeal(
      itemToClose,
      status,
      { reason: reasonId, comment },
      sourceColumnId,
      isEditMode
    );
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleCloseDeal = () => {
    if (isClosedWon) {
      setIsOpen(false);
      return closeDeal(itemToClose, status, undefined, sourceColumnId);
    }
    formRef.current?.requestSubmit();
  };

  const ModalTitle = (
    <Text as="div" variant="lgSubtitleH1">
      {t("deals.modal.title", {
        wonOrLost: capitalize(t(`deals.modal.${isClosedWon ? "won" : "lost"}`))
      })}
    </Text>
  );

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      headerClassname="border-b-0"
      contentClassname="w-[35rem]"
      data-test="deals-close-deal-modal"
      title={
        isClosedWon ? (
          <div className="min-h-2"></div>
        ) : (
          <div className="flex flex-col gap-2">{ModalTitle}</div>
        )
      }
      content={
        isClosedWon ? (
          <div className="flex flex-col gap-4 px-6 items-center">
            {ModalTitle}
            <CloseWonIllustration />
          </div>
        ) : (
          <div className="flex flex-col gap-4 w-[35rem] p-6">
            <Form
              key={`${selectedReason}-${closedLostReasonText}`}
              fields={formFields}
              isFormValid={(isValid, watchAllFields) => {
                setIsFormValid(isValid);
                const reasonValue = watchAllFields?.reason;
                setSelectedFormReasonId(
                  reasonValue ? Number(reasonValue) : null
                );
              }}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={({ setValue }) => {
                setFormValueRef.current = (name, value) =>
                  setValue(name, value, { shouldValidate: true });
              }}
            />
          </div>
        )
      }
      footer={
        <div
          className="flex justify-end gap-2"
          data-test="deals-close-deal-modal-footer"
        >
          <Button
            data-test="deals-close-deal-modal-cancel-button"
            variant="secondary"
            onClick={handleCancel}
          >
            {t("cancel")}
          </Button>
          <Button
            data-test="deals-close-deal-modal-submit-button"
            destructive={!isClosedWon}
            disabled={isClosedWon ? false : !isFormValid}
            onClick={handleCloseDeal}
          >
            {isEditMode
              ? t("save")
              : t("deals.modal.closeDeal", {
                  wonOrLost: t(`deals.modal.${isClosedWon ? "won" : "lost"}`)
                })}
          </Button>
        </div>
      }
    />
  );
};

export default CloseDealModal;
